// src/pages/PatientSelection.js
import {
  Button,
  Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow,
} from "@carbon/react";
import { useLocation, useNavigate } from "react-router-dom";
import { TrashCan, ChevronSort, ChevronUp, ChevronDown } from "@carbon/icons-react";
import CustomButton from "../components/CustomButton";
import { useAuth } from "../auth/AuthContext";
import CustomModal from "../components/CustomModal";
import React, { useMemo, useState, useEffect, useRef } from "react";
import api from "../api";

export default function PatientSelection() {
  const cancelRef = useRef(null);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { role, user } = useAuth();

  const isAP = role === "attending_physician";
  const isEP = role === "expert_physician";

  // EPの場合は sessionStorage からAP情報を拾う
  let doctorId = "";
  let doctorName = "";
  if (isEP) {
    const stored = sessionStorage.getItem("attending_doctor");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        doctorId = parsed.id ?? "";
        doctorName = parsed.text ?? "";
      } catch {
        console.warn("attending_doctor parse error");
      }
    }
  } else if (isAP) {
    doctorId = user?.id ?? "";
    doctorName = "（自分）";
  }

  const [patients, setPatients] = useState([]);

  useEffect(() => {
    sessionStorage.removeItem("patient");
  }, []);

  useEffect(() => {
    // doctorId が確定してから実行
    if (!doctorId) return;

    const fetchPatients = async () => {
      try {
        const res = await api.get(`/v1/doctor/patients/${doctorId}`);
        const data = res.data;

        // 返り値: { "patient_list": [{...}, {...}] }
        const patientsData = (data.patient_list || []).map((p) => ({
          patientId: p.research_id ?? "-",
          name: p.consultation_id ?? "-",
          emrId: p.consultation_id ?? "-", // 今は consultation_id を EMR ID 的に使う
          lastVisit: p.last_doctor_consultation ?? "", // ← typo注意
          lastAI: p.last_ai_answer ?? "",
        }));

        setPatients(patientsData);
      } catch (err) {
        console.error("患者一覧取得失敗:", err);
        setPatients([]);
      }
    };

    fetchPatients();
  }, [doctorId]);

  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const selectedPatient = useMemo(
    () => patients.find((p) => p.patientId === selectedPatientId) || null,
    [selectedPatientId, patients]
  );

  useEffect(() => {
    if (isAP) {
      navigate("/patient-selection", { replace: true });
    }
  }, [isAP, navigate]);

  useEffect(() => {
    if (isEP && !doctorId) navigate("/epselection", { replace: true });
  }, [isEP, doctorId, navigate]);

  // --- ソート（三段階: 'asc' -> 'desc' -> null） ---
  const [sort, setSort] = useState({ key: null, dir: null }); // 'patientId' | 'lastVisit' | 'lastAI' | null
  const rotateSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });
  };
  const getAriaSort = (key) => (sort.key !== key || !sort.dir ? "none" : (sort.dir === "asc" ? "ascending" : "descending"));
  const dateValue = (s, dir) => {
    if (!s) return dir === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    const t = new Date(s).getTime();
    return Number.isFinite(t) ? t : (dir === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
  };

  // ★ 電子カルテIDの検索
  const [emrQuery, setEmrQuery] = useState("");

  // 検索→ソートの順で適用
  const patientsView = useMemo(() => {
    const q = emrQuery.trim().toLowerCase();
    const base = q ? patients.filter(p => (p.emrId ?? "").toLowerCase().includes(q)) : patients;
    if (!sort.key || !sort.dir) return base;

    const dirMul = sort.dir === "asc" ? 1 : -1;
    const arr = [...base];
    arr.sort((a, b) => {
      if (sort.key === "patientId") return a.patientId.localeCompare(b.patientId, "ja") * dirMul;
      if (sort.key === "lastVisit") return (dateValue(a.lastVisit, sort.dir) - dateValue(b.lastVisit, sort.dir)) * dirMul;
      if (sort.key === "lastAI") return (dateValue(a.lastAI, sort.dir) - dateValue(b.lastAI, sort.dir)) * dirMul;
      return 0;
    });
    return arr;
  }, [patients, sort, emrQuery]);

  // 削除モーダル
  const [deleteTarget, setDeleteTarget] = useState(null);
  const requestDelete = (e, p) => { e.stopPropagation(); setDeleteTarget(p); };
  
  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      // API呼び出し
      const res = await api.delete("/v1/doctor/delete_patient_data", {
        data: {
          research_id: deleteTarget.patientId,
          att_doctor_id: doctorId,
        },
      });
      console.log("削除成功:", res.data);

      // UI更新
      setPatients(prev => prev.filter(x => x.patientId !== deleteTarget.patientId));
      setSelectedPatientId(prev => (prev === deleteTarget.patientId ? null : prev));
      setDeleteTarget(null);

      alert(res.data?.message || "患者データを削除しました。");
    } catch (err) {
      console.error("患者削除失敗:", err);
      alert("削除に失敗しました。");
    }
  };

  const handleRowClick = (pid) => setSelectedPatientId(pid);
  const handleRegister = () => navigate("/patient-registration");
  const handleNext = () => {
    if (!selectedPatient) return;
    // 患者情報をセッションストレージに保存
    sessionStorage.setItem(
      "patient",
      JSON.stringify({
        research_id: selectedPatient.patientId,
        patient_name: selectedPatient.emrId, // 表示用。別に名前情報があるならそれでもOK
      })
    );
    navigate("/summary", { state: { doctor: { id: doctorId, text: doctorName }, patient: selectedPatient } });
  };

  // ソートボタン（アイコン）
  const SortIcon = ({ col }) => {
    if (sort.key !== col || !sort.dir) return <ChevronSort size={16} />;
    return sort.dir === "asc" ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };
  const SortButton = ({ colKey, children }) => (
    <button
      type="button"
      onClick={() => rotateSort(colKey)}
      style={{ all: "unset", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
      aria-label={`ソート切替: ${children}`}
      aria-pressed={sort.key === colKey && !!sort.dir}
      title={`ソート切替: ${children}`}
    >
      <span>{children}</span>
      <SortIcon col={colKey} />
    </button>
  );

  /** ▼▼ スクロールバー幅を測って CSS 変数に反映 ▼▼ */
  const hostRef = useRef(null);
  const tbodyRef = useRef(null);

  useEffect(() => {
    const updateSBWidth = () => {
      const tb = tbodyRef.current;
      const host = hostRef.current;
      if (!tb || !host) return;
      const sbw = tb.offsetWidth - tb.clientWidth; // px
      host.style.setProperty("--sbw", `${sbw}px`);
    };
    updateSBWidth();
    window.addEventListener("resize", updateSBWidth);
    return () => window.removeEventListener("resize", updateSBWidth);
  }, [patientsView.length]);
  /** ▲▲ */

  useEffect(() => {
    if (selectedPatientId) {
      // スムーズにページ最下部までスクロール
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [selectedPatientId]);

  return (
    <>
      <div style={{ padding: "1rem", maxWidth: 1000, margin: "0 auto" }}>
        <h1 style={{ textAlign: "center", marginBottom: "0.5rem" }}>
          患者一覧{doctorName ? `（主治医：${doctorName}）` : ""}
        </h1>
        <div style={{ textAlign: "center", color: "#525252" }}>
          行をクリックして患者を選択してください
        </div>

        <Stack gap={7}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1rem" }}>
            {isAP ? <CustomButton onClick={handleRegister}>新規登録</CustomButton> : <div />}
            <div />
          </div>

          {/* 1テーブルのまま、tbodyだけをスクロール */}
          <div className="table-scroll-host" ref={hostRef}>
            <TableContainer>
              <Table size="md" style={{ tableLayout: "fixed", minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableHeader>選択中</TableHeader>
                    <TableHeader aria-sort={getAriaSort("patientId")}>
                      <SortButton colKey="patientId">患者ID</SortButton>
                    </TableHeader>
                    <TableHeader>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <span>電子カルテID</span>
                        <input
                          className="emr-search"
                          type="text"
                          value={emrQuery}
                          onChange={(e) => setEmrQuery(e.target.value)}
                          placeholder="IDで検索"
                          aria-label="電子カルテIDで検索"
                        />
                      </div>
                    </TableHeader>
                    <TableHeader aria-sort={getAriaSort("lastVisit")}>
                      <SortButton colKey="lastVisit">最終診察日</SortButton>
                    </TableHeader>
                    <TableHeader aria-sort={getAriaSort("lastAI")}>
                      <SortButton colKey="lastAI">AI問診最終回答日</SortButton>
                    </TableHeader>
                    {isAP && <TableHeader>操作</TableHeader>}
                  </TableRow>
                </TableHead>

                <TableBody ref={tbodyRef}>
                  {patientsView.map((p) => {
                    const isSelected = p.patientId === selectedPatientId;
                    return (
                      <TableRow
                        key={p.patientId}
                        onClick={() => handleRowClick(p.patientId)}
                        style={{ cursor: "pointer", backgroundColor: isSelected ? "#e5f1ff" : undefined }}
                      >
                        <TableCell>
                          <div
                            aria-hidden
                            style={{
                              width: 14, height: 14, borderRadius: "50%",
                              border: "2px solid #0f62fe",
                              background: isSelected ? "#0f62fe" : "transparent",
                            }}
                          />
                        </TableCell>
                        <TableCell>{p.patientId}</TableCell>
                        <TableCell>{p.emrId}</TableCell>
                        <TableCell>{p.lastVisit || <span style={{ color: "#bbb" }}>—</span>}</TableCell>
                        <TableCell>{p.lastAI || <span style={{ color: "#bbb" }}>—</span>}</TableCell>
                        {isAP && (
                          <TableCell>
                            <Button
                              kind="ghost"
                              hasIconOnly
                              size="sm"
                              renderIcon={TrashCan}
                              iconDescription="削除"
                              onClick={(e) => requestDelete(e, p)}
                              tooltipPosition="right"
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {patientsView.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAP ? 6 : 5}>患者が見つかりません。</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {selectedPatient && (
            <div
              style={{
                marginTop: "0.5rem",
                fontSize: "0.95rem",
                color: "#161616",
                background: "#f4f4f4",
                padding: "0.75rem 1rem",
                borderLeft: "4px solid #0f62fe",
              }}
            >
              選択中：{selectedPatient.patientId}｜{selectedPatient.emrId}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <CustomButton kind="primary" onClick={handleNext} disabled={!selectedPatient}>
              次へ
            </CustomButton>
          </div>
        </Stack>
      </div>

      {/* 削除確認モーダル */}
      <CustomModal
        open={!!deleteTarget}
        title="患者を削除しますか？"
        onClose={() => setDeleteTarget(null)}
        size="sm"
        closeOnOverlay
        initialFocusRef={cancelRef}
        footer={
          <>
            <CustomButton kind="secondary" onClick={() => setDeleteTarget(null)} ref={cancelRef} data-autofocus>
              キャンセル
            </CustomButton>
            <CustomButton kind="danger" onClick={confirmDelete}>
              削除する
            </CustomButton>
          </>
        }
      >
        {deleteTarget && (
          <p style={{ marginTop: 0, paddingLeft: 16 }}>
            {deleteTarget.patientId}（{deleteTarget.name ?? "—"}）を削除します。よろしいですか？
          </p>
        )}
      </CustomModal>

      <style>{`
        .table-scroll-host {
          /* 列幅（必要ならここだけいじれば全体に効く） */
          --w-sel: 56px;
          --w-id: 128px;
          --w-emr: 176px;
          --w-v: 148px;
          --w-ai: 176px;
          --w-ops: 84px;
        }

        .table-scroll-host table {
          table-layout: fixed;
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        /* thead と tbody を分離、幅は100%のまま */
        .table-scroll-host thead {
          display: table;
          width: 100%;
          table-layout: fixed;
          box-sizing: border-box;
        }
        .table-scroll-host tbody {
          display: block;
          max-height: 420px;
          overflow: auto;
          width: 100%;
          scrollbar-gutter: stable;

          /* ← スクロールバーを非表示にする（幅0扱い） */
          scrollbar-width: none;          /* Firefox */
          -ms-overflow-style: none;       /* IE/旧Edge */
        }
        .table-scroll-host tbody::-webkit-scrollbar {
          width: 0;
          height: 0;
        }

        .table-scroll-host tbody tr {
          display: table;
          width: 100%;
          table-layout: fixed;
        }

        /* すべて左揃え & パディング完全一致 */
        .table-scroll-host th,
        .table-scroll-host td {
          text-align: left;
          box-sizing: border-box;
          padding-left: 16px !important;
          padding-right: 16px !important;
          vertical-align: middle;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .table-scroll-host thead th { height: 48px; }

        /* 列幅固定（thead/tbody 両方に適用） */
        .table-scroll-host thead th:nth-child(1),
        .table-scroll-host tbody td:nth-child(1) { width: var(--w-sel); }
        .table-scroll-host thead th:nth-child(2),
        .table-scroll-host tbody td:nth-child(2) { width: var(--w-id); }
        .table-scroll-host thead th:nth-child(3),
        .table-scroll-host tbody td:nth-child(3) { width: var(--w-emr); }
        .table-scroll-host thead th:nth-child(4),
        .table-scroll-host tbody td:nth-child(4) { width: var(--w-v); }
        .table-scroll-host thead th:nth-child(5),
        .table-scroll-host tbody td:nth-child(5) { width: var(--w-ai); }
        .table-scroll-host thead th:nth-child(6),
        .table-scroll-host tbody td:nth-child(6) { width: var(--w-ops); }

        /* 電子カルテID検索入力（ヘッダー内） */
        .table-scroll-host thead .emr-search {
          width: 100%;
          box-sizing: border-box;
          height: 28px;
          padding: 4px 8px;
          font-size: 0.875rem;
          border: 1px solid #8d8d8d;
          outline: none;
        }
        .table-scroll-host thead .emr-search:focus {
          border-color: #0f62fe;
          box-shadow: 0 0 0 2px rgba(15, 98, 254, 0.2);
        }
      `}</style>
    </>
  );
}
